import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { getBullyingConfig, updateBullyingConfig, dismissBullyingWarning } from "../../lib/api";

const PRESETS = ["30", "60", "90", "180", "365"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function pad(n) { return String(n).padStart(2, "0"); }

// Map stored config -> the single "window choice" dropdown value.
function choiceOf(cfg) {
  if (!cfg) return "school_year";
  if (cfg.window_mode === "school_year") return "school_year";
  const d = String(cfg.rolling_days);
  return PRESETS.includes(d) ? d : "custom";
}

export default function BullyingDetectionTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState(null);   // array of dropped warnings or null

  const load = async () => {
    try {
      const { data } = await getBullyingConfig();
      setCfg({
        window_mode: data.window_mode || "school_year",
        rolling_days: data.rolling_days ?? 90,
        school_year_start_month: data.school_year_start_month ?? 8,
        school_year_start_day: data.school_year_start_day ?? 1,
        warning_threshold: data.warning_threshold ?? 2,
        investigation_threshold: data.investigation_threshold ?? 3,
        multi_aggressor_threshold: data.multi_aggressor_threshold ?? 3,
        auto_open_bullying: !!data.auto_open_bullying,
        auto_open_harassment: !!data.auto_open_harassment,
        auto_open_hazing: !!data.auto_open_hazing,
      });
    } catch { toast.error("Failed to load detection settings"); }
  };
  useEffect(() => { load(); }, []);

  const choice = choiceOf(cfg);
  const setChoice = (v) => {
    if (v === "school_year") setCfg((p) => ({ ...p, window_mode: "school_year" }));
    else if (v === "custom") setCfg((p) => ({ ...p, window_mode: "rolling" }));
    else setCfg((p) => ({ ...p, window_mode: "rolling", rolling_days: Number(v) }));
  };

  // school-year start as a date input (year is irrelevant; use leap year 2000).
  const startDateValue = cfg ? `2000-${pad(cfg.school_year_start_month)}-${pad(cfg.school_year_start_day)}` : "";
  const onStartDate = (e) => {
    const parts = (e.target.value || "").split("-");
    if (parts.length === 3) {
      setCfg((p) => ({ ...p, school_year_start_month: Number(parts[1]), school_year_start_day: Number(parts[2]) }));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await updateBullyingConfig(cfg);
      setCfg((p) => ({ ...p, ...(data.config || {}) }));
      const dropped = data.review || [];
      if (dropped.length) {
        setReview(dropped);
        toast.warning(`${dropped.length} warning(s) no longer qualify under the new window — please review.`);
      } else {
        toast.success("Detection settings saved");
      }
    } catch { toast.error("Failed to save settings"); }
    setSaving(false);
  };

  if (!cfg) return <div className="h-40 bg-gray-100 rounded animate-pulse" />;

  const plain = cfg.window_mode === "school_year"
    ? `Current school year (starts ${MONTHS[cfg.school_year_start_month - 1]} ${cfg.school_year_start_day} each year).`
    : `A rolling window of the last ${cfg.rolling_days} days.`;

  return (
    <Card data-testid="bullying-detection-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#00305E]" /> Bullying &amp; Harassment Detection</CardTitle>
        <CardDescription>
          Controls the time window used to count repeated victim–aggressor patterns in incident reports, plus the thresholds that raise a warning or open an investigation. The system only flags patterns for human review.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Window */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Counting window</Label>
              <Select value={choice} onValueChange={setChoice}>
                <SelectTrigger data-testid="bd-window-choice"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="school_year" data-testid="bd-window-choice-school_year">Current school year</SelectItem>
                  <SelectItem value="30" data-testid="bd-window-choice-30">Rolling — last 30 days</SelectItem>
                  <SelectItem value="60" data-testid="bd-window-choice-60">Rolling — last 60 days</SelectItem>
                  <SelectItem value="90" data-testid="bd-window-choice-90">Rolling — last 90 days</SelectItem>
                  <SelectItem value="180" data-testid="bd-window-choice-180">Rolling — last 180 days</SelectItem>
                  <SelectItem value="365" data-testid="bd-window-choice-365">Rolling — last 365 days</SelectItem>
                  <SelectItem value="custom" data-testid="bd-window-choice-custom">Custom number of days…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {choice === "school_year" && (
              <div className="space-y-1.5">
                <Label>School year starts on</Label>
                <Input type="date" value={startDateValue} onChange={onStartDate} data-testid="bd-school-year-start" />
                <p className="text-xs text-muted-foreground">Only the month and day are used (the year is ignored).</p>
              </div>
            )}
            {choice === "custom" && (
              <div className="space-y-1.5">
                <Label>Number of days</Label>
                <Input type="number" min={1} value={cfg.rolling_days} data-testid="bd-custom-days"
                  onChange={(e) => setCfg((p) => ({ ...p, rolling_days: e.target.value }))} />
              </div>
            )}
          </div>
          <p className="text-sm text-slate-600 bg-slate-50 border rounded px-3 py-2" data-testid="bd-window-plain">
            Active window: {plain}
          </p>
        </div>

        {/* Thresholds */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>Warning at (incidents)</Label>
            <Input type="number" min={1} value={cfg.warning_threshold} data-testid="bd-warning-threshold"
              onChange={(e) => setCfg((p) => ({ ...p, warning_threshold: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Investigation at (incidents)</Label>
            <Input type="number" min={1} value={cfg.investigation_threshold} data-testid="bd-investigation-threshold"
              onChange={(e) => setCfg((p) => ({ ...p, investigation_threshold: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Multi-aggressor warning at</Label>
            <Input type="number" min={1} value={cfg.multi_aggressor_threshold} data-testid="bd-multi-threshold"
              onChange={(e) => setCfg((p) => ({ ...p, multi_aggressor_threshold: e.target.value }))} />
          </div>
        </div>

        {/* Auto-open by incident type */}
        <div className="space-y-2 border-t pt-4">
          <Label className="font-semibold">Auto-open an investigation by incident type</Label>
          <p className="text-xs text-muted-foreground">
            Harassment and hazing complaints often require an investigation after a single event. When checked, an investigation opens automatically on the FIRST incident tagged with that HHB concern — no repeat incidents needed. All off by default.
          </p>
          <div className="flex flex-col gap-2 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bd-auto-open-harassment">
              <input type="checkbox" checked={!!cfg.auto_open_harassment}
                onChange={(e) => setCfg((p) => ({ ...p, auto_open_harassment: e.target.checked }))} />
              Automatically open an investigation on the first <span className="font-medium">Harassment</span> incident
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bd-auto-open-hazing">
              <input type="checkbox" checked={!!cfg.auto_open_hazing}
                onChange={(e) => setCfg((p) => ({ ...p, auto_open_hazing: e.target.checked }))} />
              Automatically open an investigation on the first <span className="font-medium">Hazing</span> incident
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bd-auto-open-bullying">
              <input type="checkbox" checked={!!cfg.auto_open_bullying}
                onChange={(e) => setCfg((p) => ({ ...p, auto_open_bullying: e.target.checked }))} />
              Automatically open an investigation on the first <span className="font-medium">Bullying</span> incident
            </label>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="bd-save">
          {saving ? "Saving…" : "Save Detection Settings"}
        </Button>

        <p className="text-xs text-muted-foreground">
          Changing the window recalculates all pattern counts immediately. Existing warnings and open investigations are never closed or deleted automatically — any that no longer qualify under the new window are listed for you to review and dismiss manually.
        </p>
      </CardContent>

      <WindowChangeReviewDialog review={review} onClose={() => setReview(null)} />
    </Card>
  );
}

function WindowChangeReviewDialog({ review, onClose }) {
  const [items, setItems] = useState([]);
  const [reasons, setReasons] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => { setItems(review || []); setReasons({}); }, [review]);

  const dismiss = async (it) => {
    const reason = (reasons[it.pair_key] || "").trim();
    if (!reason) { toast.error("A reason is required to dismiss"); return; }
    setBusy(it.pair_key);
    try {
      await dismissBullyingWarning(it.pair_key, reason);
      toast.success("Dismissed and logged");
      setItems((p) => p.filter((x) => x.pair_key !== it.pair_key));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to dismiss"); }
    setBusy("");
  };

  return (
    <Dialog open={!!review} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bd-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Warnings no longer qualifying</DialogTitle>
          <DialogDescription>
            After the window change, these previously-flagged patterns no longer meet the threshold. Nothing has been closed or deleted. Review each one and dismiss it with a reason for the audit trail, or leave it for later.
          </DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="bd-review-empty">All reviewed. You can close this.</p>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.pair_key} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid={`bd-review-${it.pair_key}`}>
                <p className="text-sm">
                  {it.kind === "multi_aggressor" ? (
                    <><span className="font-semibold">{it.victim_name}</span> targeted by multiple students</>
                  ) : (
                    <><span className="font-semibold">{it.victim_name}</span> targeted by <span className="font-semibold">{it.aggressor_name}</span></>
                  )}
                  {" "}— was a <span className="font-medium">{it.was}</span> ({it.old_count} incidents), now <span className="font-medium">{it.new_count}</span> under the new window.
                </p>
                <textarea rows={2} value={reasons[it.pair_key] || ""} data-testid={`bd-review-reason-${it.pair_key}`}
                  onChange={(e) => setReasons((p) => ({ ...p, [it.pair_key]: e.target.value }))}
                  placeholder="Reason for dismissing (logged)…" className="w-full text-sm border rounded px-2 py-1.5" />
                <Button size="sm" variant="outline" disabled={busy === it.pair_key}
                  onClick={() => dismiss(it)} data-testid={`bd-review-dismiss-${it.pair_key}`}>
                  {busy === it.pair_key ? "Dismissing…" : "Dismiss with reason"}
                </Button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bd-review-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
